import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Eye, EyeOff, Upload, X, Save } from 'lucide-react';
import ApiService from '../../services/api';
import {
  DAY_LABELS,
  DEFAULT_WORKING_DAYS,
  formatWorkingDaysSummary,
  parseWorkingDays
} from '../../utils/teamMemberSchedule';

interface TeamMember {
  id: number;
  name: string;
  specialization: string;
  imageUrl?: string;
  displayOrder: number;
  isActive: boolean;
  workingDays?: number[] | string;
  sundayStartHour?: number;
  createdAt: string;
  updatedAt: string;
  teamMemberCategories?: TeamMemberCategory[];
}

const DEFAULT_FORM_WORKING_DAYS = [...DEFAULT_WORKING_DAYS];

interface TeamMemberCategory {
  id: number;
  teamMemberId: number;
  categoryId: number;
  isActive: boolean;
  category?: Category;
}

interface Category {
  id: number;
  name: string;
  displayName: string;
  icon: string;
  isActive: boolean;
}

const TeamManagement: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    specialization: '',
    displayOrder: 0,
    workingDays: [...DEFAULT_FORM_WORKING_DAYS] as number[],
    sundayStartHour: 11
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Load team members
  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAdminTeamMembers();
      setTeamMembers(response.teamMembers || []);
    } catch (error: any) {
      console.error('Team members load error:', error);
      setError('Ekip üyeleri yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load categories
  const loadCategories = async () => {
    try {
      const response = await ApiService.getCategories();
      setCategories(response.categories || []);
    } catch (error: any) {
      console.error('Categories load error:', error);
    }
  };

  useEffect(() => {
    loadTeamMembers();
    loadCategories();
    
    // Home settings yükle
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response.settings);
      } catch (error) {
        console.error('Home settings yüklenemedi:', error);
      }
    };
    loadHomeSettings();
  }, []);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      specialization: '',
      displayOrder: 0,
      workingDays: [...DEFAULT_FORM_WORKING_DAYS],
      sundayStartHour: 11
    });
    setSelectedImage(null);
    setImagePreview(null);
    setShowAddForm(false);
    setEditingMember(null);
  };

  // Handle image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Dosya boyutu 5MB\'dan küçük olmalıdır.');
        return;
      }
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle category management
  const handleCategoryModal = (member: TeamMember) => {
    setSelectedTeamMember(member);
    setShowCategoryModal(true);
  };

  const addCategoryToMember = async (categoryId: number) => {
    if (!selectedTeamMember) return;
    
    try {
      await ApiService.addTeamMemberCategory(selectedTeamMember.id, categoryId);
      // Refresh team member data
      const updatedMember = await ApiService.getTeamMemberCategories(selectedTeamMember.id);
      if (updatedMember.teamMember) {
        setTeamMembers(prev => prev.map(member => 
          member.id === selectedTeamMember.id ? updatedMember.teamMember : member
        ));
        setSelectedTeamMember(updatedMember.teamMember);
      }
    } catch (error: any) {
      setError('Kategori eklenemedi: ' + error.message);
    }
  };

  const removeCategoryFromMember = async (categoryId: number) => {
    if (!selectedTeamMember) return;
    
    try {
      await ApiService.removeTeamMemberCategory(selectedTeamMember.id, categoryId);
      // Refresh team member data
      const updatedMember = await ApiService.getTeamMemberCategories(selectedTeamMember.id);
      if (updatedMember.teamMember) {
        setTeamMembers(prev => prev.map(member => 
          member.id === selectedTeamMember.id ? updatedMember.teamMember : member
        ));
        setSelectedTeamMember(updatedMember.teamMember);
      }
    } catch (error: any) {
      setError('Kategori kaldırılamadı: ' + error.message);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.specialization.trim()) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    if (formData.workingDays.length === 0) {
      setError('En az bir çalışma günü seçmelisiniz.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitFormData = new FormData();
      submitFormData.append('name', formData.name.trim());
      submitFormData.append('specialization', formData.specialization.trim());
      submitFormData.append('displayOrder', formData.displayOrder.toString());
      submitFormData.append('workingDays', JSON.stringify(formData.workingDays));
      submitFormData.append('sundayStartHour', formData.sundayStartHour.toString());
      
      if (selectedImage) {
        submitFormData.append('image', selectedImage);
      }

      if (editingMember) {
        await ApiService.updateTeamMember(editingMember.id, submitFormData);
      } else {
        await ApiService.createTeamMember(submitFormData);
      }

      resetForm();
      loadTeamMembers();
    } catch (error: any) {
      console.error('Team member save error:', error);
      setError('Ekip üyesi kaydedilemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkingDay = (day: number) => {
    setFormData((prev) => {
      const hasDay = prev.workingDays.includes(day);
      const workingDays = hasDay
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day].sort((a, b) => a - b);

      return { ...prev, workingDays };
    });
  };

  const formatWorkingDays = (member: TeamMember) => formatWorkingDaysSummary(member);

  // Handle edit
  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      specialization: member.specialization,
      displayOrder: member.displayOrder,
      workingDays: parseWorkingDays(member),
      sundayStartHour: member.sundayStartHour ?? 11
    });
    setImagePreview(member.imageUrl ? `http://localhost:5000/api/proxy/uploads/team/${member.imageUrl.split('/').pop()}` : null);
    setShowAddForm(true);
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu ekip üyesini silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteTeamMember(id);
      loadTeamMembers();
    } catch (error: any) {
      console.error('Team member delete error:', error);
      setError('Ekip üyesi silinemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle active
  const handleToggleActive = async (id: number) => {
    try {
      await ApiService.toggleTeamMemberActive(id);
      loadTeamMembers();
    } catch (error: any) {
      console.error('Team member toggle error:', error);
      setError('Ekip üyesi durumu değiştirilemedi: ' + error.message);
    }
  };

  // Get image URL
  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkY5M0E2Ii8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
    }
    if (imageUrl.startsWith('/uploads/team/')) {
      const filename = imageUrl.replace('/uploads/team/', '');
      return `http://localhost:5000/api/proxy/uploads/team/${filename}`;
    }
    return imageUrl;
  };

  return (
    <div className="space-y-6 text-white">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Ekip Yönetimi</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-md hover:from-pink-600 hover:to-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Ekip Üyesi</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-700 hover:text-red-900"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="rounded-lg shadow-sm p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingMember ? 'Ekip Üyesini Düzenle' : 'Yeni Ekip Üyesi Ekle'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  İsim *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                  style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  placeholder="Çalışan adı"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Uzmanlık Alanı *
                </label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                  style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                  placeholder="Uzmanlık alanı"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Çalışma Günleri *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DAY_LABELS.map((label, day) => (
                  <label
                    key={day}
                    className="flex items-center space-x-2 px-3 py-2 border border-gray-600 rounded-md cursor-pointer hover:border-pink-500"
                  >
                    <input
                      type="checkbox"
                      checked={formData.workingDays.includes(day)}
                      onChange={() => toggleWorkingDay(day)}
                      className="rounded border-gray-300 text-pink-500 focus:ring-pink-500"
                    />
                    <span className="text-sm text-gray-300">{label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Varsayılan: Pazartesi kapalı, Pazar 11:00&apos;de başlar
              </p>
            </div>

            {formData.workingDays.includes(0) && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pazar Başlangıç Saati
                </label>
                <select
                  value={formData.sundayStartHour}
                  onChange={(e) => setFormData({ ...formData, sundayStartHour: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                  style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                >
                  <option value={9}>09:00</option>
                  <option value={10}>10:00</option>
                  <option value={11}>11:00</option>
                  <option value={12}>12:00</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Gösterim Sırası
              </label>
              <input
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                placeholder="0"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Fotoğraf
              </label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleImageSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-white"
                style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-md"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-md hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>{loading ? 'Kaydediliyor...' : (editingMember ? 'Güncelle' : 'Ekle')}</span>
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members List */}
              <div className="rounded-lg shadow-sm overflow-hidden border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
                  <div className="px-6 py-4 border-b border-gray-700">
                          <h3 className="text-lg font-semibold text-white">Ekip Üyeleri</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">Yükleniyor...</p>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Henüz ekip üyesi eklenmemiş</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-700">
              <thead style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fotoğraf
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İsim
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uzmanlık
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Çalışma Günleri
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sıra
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
                {teamMembers.map((member) => (
                                      <tr key={member.id} className="hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <img
                        src={getImageUrl(member.imageUrl)}
                        alt={member.name}
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkY5M0E2Ii8+CjxwYXRoIGQ9Ik0xMDAgODBDMTA4LjgzNyA4MCAxMTYgODcuMTYzNCAxMTYgOTZDMTE2IDEwNC44MzcgMTA4LjgzNyAxMTIgMTAwIDExMkM5MS4xNjM0IDExMiA4NCAxMDQuODM3IDg0IDk2Qzg0IDg3LjE2MzQgOTEuMTYzNCA4MCAxMDAgODBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTAwIDEyOEMxMTYuNTY5IDEyOCAxMzAgMTQxLjQzMSAxMzAgMTU4VjE2MEMxMzAgMTY2LjYyNyAxMjQuNjI3IDE3MiAxMTggMTcySDgyQzc1LjM3MjYgMTcyIDcwIDE2Ni42MjcgNzAgMTYwVjE1OEM3MCAxNDEuNDMxIDgzLjQzMTQgMTI4IDEwMCAxMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{member.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{member.specialization}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-400 max-w-xs">{formatWorkingDays(member)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{member.displayOrder}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(member.id)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {member.isActive ? (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Aktif
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3 mr-1" />
                            Pasif
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(member)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Düzenle"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCategoryModal(member)}
                          className="text-green-600 hover:text-green-900"
                          title="Kategorileri Yönet"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Category Management Modal */}
        {showCategoryModal && selectedTeamMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {selectedTeamMember.name} - Kategori Yönetimi
                  </h3>
                  <button
                    onClick={() => {
                      setShowCategoryModal(false);
                      setSelectedTeamMember(null);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Mevcut Kategoriler</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedTeamMember.teamMemberCategories && selectedTeamMember.teamMemberCategories.length > 0 ? (
                      selectedTeamMember.teamMemberCategories.map((tmc) => (
                        <div key={tmc.id} className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                          <span>{tmc.category?.displayName || 'Bilinmeyen Kategori'}</span>
                          <button
                            onClick={() => removeCategoryFromMember(tmc.categoryId)}
                            className="ml-2 text-green-600 hover:text-green-800"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500">Henüz kategori atanmamış</p>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Kategori Ekle</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {categories
                      .filter(category => 
                        !selectedTeamMember.teamMemberCategories?.some(tmc => 
                          tmc.categoryId === category.id && tmc.isActive
                        )
                      )
                      .map((category) => (
                        <button
                          key={category.id}
                          onClick={() => addCategoryToMember(category.id)}
                          className="flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-left"
                        >
                          <div className="flex items-center">
                            <span className="text-lg mr-2">{category.icon}</span>
                            <span className="font-medium">{category.displayName}</span>
                          </div>
                          <Plus className="w-4 h-4 text-gray-400" />
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
